/**
 * En-tête mobile commun à toutes les pages.
 *
 * AppShell affiche une seule barre en haut sur mobile : ☰ (ou ←) · 🔍 à gauche, titre centré,
 * actions de la page à droite (2 maximum). Chaque page y place son titre et ses actions
 * avec <MobileHeader>, rendu via un portail dans cette barre :
 *
 *   <MobileHeader title="Élèves" count={16} actions={<HeaderAction icon={Plus} label="Ajouter" onClick={...} />} />
 *   <MobileHeader title={course.name} back="/group-classes" />
 *
 * Sur PC, rien n'est affiché : la page garde son propre titre.
 * Pour masquer un élément de la page sur mobile seulement, utiliser la classe `mobile-hidden`.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Menu, Search, X, Banknote, Plus } from 'lucide-react';
import { cn } from '../lib/utils';

export const MobileHeaderContext = createContext(null);

/** Icône « caisse » : un billet avec un petit + (encaisser). */
export function BanknotePlus({ size = 20 }) {
  return (
    <span className="relative inline-flex">
      <Banknote size={size} />
      <Plus size={Math.round(size * 0.55)} strokeWidth={3.5} className="absolute -top-1.5 -right-2" />
    </span>
  );
}

/**
 * Boutons valables sur toutes les pages, à gauche de la barre : ☰ (ou ←) · 🔍 recherche · 💵+ caisse (admin).
 * `onSearch` : action de la loupe (par défaut, la recherche globale).
 */
export function LeadingButtons({ back, onSearch }) {
  const ctx = useContext(MobileHeaderContext);
  const navigate = useNavigate();
  return (
    <>
      {back ? (
        <HeaderAction icon={ArrowLeft} label="Retour" onClick={() => (typeof back === 'string' ? navigate(back) : navigate(-1))} />
      ) : (
        <span className="align-hide contents"><HeaderAction icon={Menu} label="Menu" onClick={ctx.openMenu} testId="hamburger-menu-btn" /></span>
      )}
      {/* 🔍 « Chercher & encaisser » : taper un montant propose « Encaisser » ; la pastille = paiements à attribuer */}
      <HeaderAction icon={Search} label="Rechercher" onClick={onSearch || (() => ctx.openSearch())} testId="header-search-btn"
        dot={ctx.isAdmin && ctx.pendingCount > 0} />
    </>
  );
}

/** Montant tapé dans la recherche (« 40 », « 25.50 », « 40 chf », « 40.- ») → nombre, sinon null. */
export function parseAmount(q) {
  const m = String(q || '').trim().match(/^([1-9]\d{0,3})(?:[.,](\d{1,2}))?\s*(?:chf|fr\.?|\.-|-)?$/i);
  return m ? parseFloat(`${m[1]}.${m[2] || 0}`) : null;
}

/** Affichage d'un montant : « 40 », « 25.50 ». */
export const fmtAmount = (n) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(2));

/**
 * `search` (optionnel) : { value, onChange, placeholder, scope } — la page a sa propre liste :
 * la loupe filtre alors la page elle-même (affichage habituel de la page) ; la ligne d'actions
 * d'AppShell propose « Encaisser » (si un montant est tapé), « À attribuer » et « Aussi : … ».
 * Sans `search`, la loupe ouvre la recherche globale.
 * `scope` : type de la page dans la recherche globale (students, courses, festivals, tasks…).
 * Le titre est centré sur la ligne : au plus 2 actions à droite.
 */
export function MobileHeader({ title, count, back, actions, search }) {
  const ctx = useContext(MobileHeaderContext);
  const register = ctx?.register;
  const [searching, setSearching] = useState(false);
  const setPageSearch = ctx?.setPageSearch;
  const active = !!search && (searching || !!search.value);
  const pageQuery = search ? search.value : '';
  const pageScope = search?.scope;
  useEffect(() => register?.(), [register]);
  // Publie la recherche de la page pour la ligne d'actions d'AppShell
  useEffect(() => {
    setPageSearch?.(active ? { q: pageQuery, scope: pageScope } : null);
  }, [active, pageQuery, pageScope, setPageSearch]);
  useEffect(() => () => setPageSearch?.(null), [setPageSearch]);
  if (!ctx?.slot) return null;

  if (active) {
    return createPortal(
      <>
        <Search size={18} className="ml-2.5 mr-1 text-slate-400 shrink-0" />
        <input autoFocus value={search.value} onChange={e => search.onChange(e.target.value)}
          placeholder={search.placeholder || 'Rechercher…'} data-testid="header-search-input"
          className="flex-1 min-w-0 h-10 bg-transparent text-base text-slate-900 outline-none" />
        <HeaderAction icon={X} label="Fermer la recherche" testId="header-search-close"
          onClick={() => { search.onChange(''); setSearching(false); }} />
      </>,
      ctx.slot
    );
  }

  return createPortal(
    <>
      <LeadingButtons back={back} onSearch={search ? () => setSearching(true) : undefined} />
      {/* Titre centré sur l'écran ; la place réservée de chaque côté = 2 boutons */}
      <h1 className="absolute left-1/2 -translate-x-1/2 max-w-[calc(100%-184px)] truncate text-center text-lg font-extrabold text-slate-900 font-manrope pointer-events-none">
        {title}
        {count != null && <span className="ml-1.5 text-sm font-semibold text-slate-400">{count}</span>}
      </h1>
      <div className="flex-1" />
      {actions && <div className="flex items-center gap-1 shrink-0">{actions}</div>}
    </>,
    ctx.slot
  );
}

/** Indique à la caisse le cours affiché (ses élèves apparaissent en premier). */
export function useCashboxCourse(courseId) {
  const setCashboxCourse = useContext(MobileHeaderContext)?.setCashboxCourse;
  useEffect(() => {
    if (!courseId || !setCashboxCourse) return;
    setCashboxCourse(courseId);
    return () => setCashboxCourse(null);
  }, [courseId, setCashboxCourse]);
}

/**
 * Bouton icône standard de la barre du haut (40×40).
 * `primary` = action principale (fond plein, bleu par défaut ; `color` pour une autre couleur, ex. vert pour la caisse).
 */
export function HeaderAction({ icon: Icon, label, onClick, primary = false, color, active = false, badge, dot = false, testId }) {
  return (
    <button onClick={onClick} aria-label={label} title={label} data-testid={testId}
      style={primary && color ? { backgroundColor: color } : undefined}
      className={cn('relative w-10 h-10 shrink-0 flex items-center justify-center rounded-xl transition-colors',
        primary ? 'bg-[#0288D1] text-white active:opacity-80'
          : active ? 'bg-[#0288D1]/10 text-[#0288D1]'
          : 'text-slate-600 active:bg-slate-100')}>
      <Icon size={20} />
      {badge ? (
        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#0288D1] text-white text-[10px] font-bold flex items-center justify-center">
          {badge}
        </span>
      ) : null}
      {dot && <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-amber-500 ring-2 ring-white" data-testid={testId && `${testId}-dot`} />}
    </button>
  );
}

/** Barre d'outils mobile standard sous l'en-tête : une ligne de puces qui défile horizontalement. */
export function MobileToolbar({ children, className }) {
  return (
    <div className={cn('mobile-only -mx-4 px-4 mb-3 flex items-center gap-2 overflow-x-auto no-scrollbar', className)}>
      {children}
    </div>
  );
}

/** Puce standard (filtre rapide, vue, tri…). */
export function Chip({ active, onClick, children, icon: Icon, color, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={cn('shrink-0 h-9 px-3.5 rounded-full text-sm font-semibold border flex items-center gap-1.5 whitespace-nowrap transition-colors',
        active ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-200 active:bg-slate-50')}
      style={active ? { backgroundColor: color || '#0288D1' } : undefined}>
      {Icon && <Icon size={15} />}
      {children}
    </button>
  );
}

/**
 * Menu d'actions qui monte du bas de l'écran (style iOS).
 * actions : [{ icon, label, onClick, className }] — le menu se ferme avant d'exécuter l'action.
 */
export function ActionSheet({ open, onClose, title, actions = [], children, testId }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-full max-h-[85vh] overflow-y-auto bg-white rounded-t-3xl p-3 shadow-2xl" onClick={e => e.stopPropagation()}
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }} data-testid={testId}>
        <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-3" />
        {title && <div className="px-4 pb-2 text-sm font-semibold text-slate-500">{title}</div>}
        {actions.map(({ icon: Icon, label, onClick, className }) => (
          <button key={label} onClick={() => { onClose(); onClick(); }}
            className={cn('w-full flex items-center gap-3 px-4 h-14 rounded-xl text-base font-semibold text-slate-700 active:bg-slate-100', className)}>
            {Icon && <Icon size={20} />} {label}
          </button>
        ))}
        {children}
        <button className="w-full h-12 mt-2 rounded-xl bg-slate-100 text-slate-600 font-semibold" onClick={onClose}>Fermer</button>
      </div>
    </div>
  );
}

/** Accès aux fonctions communes d'AppShell (recherche, caisse, paiements à attribuer). */
export function useShell() {
  return useContext(MobileHeaderContext) || {};
}
